/**
 * ============================================================================
 * PlayerImport.js — "📥 Import players" on Admin → Players (Sept 30, 2026)
 * ============================================================================
 * Bring in a whole list of golfers at once: an event field, a club roster, a
 * registration export. Three steps in one window:
 *
 *   1. Choose the list: a CSV / Excel file, or paste rows copied from a
 *      spreadsheet. A blank template is offered.
 *   2. Check it: the columns TTO recognized (First / Last or Name, Email, Phone,
 *      GHIN, Handicap Index, Gender), each changeable; who they come in as
 *      (Guests or Regular players); optionally which tee time to put them on.
 *      Every change re-asks the server with dry_run, so the counts and the per-row
 *      status ("New", "Already in your list", "Skipped") are exactly what the
 *      import will do.
 *   3. Done: what was added, updated, skipped, and placed on the tee time.
 *
 * The server does the work (routes/playerImportRoutes.js): matching by email,
 * GHIN or a unique name, filling blanks only, the plan's size for regular
 * players, and the tee time's spots / Event Pass field size.
 *
 * Excel is read with SheetJS from cdnjs, loaded only when an Excel file is chosen
 * (the Upload HCPs window does the same).
 *
 * Teams (Oct 10, 2026 — Dennis: "accept both shapes"). A team list arrives as
 *   - one row per golfer with a Team column (and optionally Captain: Y), or
 *   - one row per team with the players across (Player 1, Player 2, …, each with
 *     its own email / handicap columns if the list has them).
 * The layout is detected from the headings and can be changed. Either way the
 * screen sends one row per golfer carrying its team; the server forms the teams,
 * holds a spot for each blank name and places each team as a unit on the tee time.
 * ============================================================================
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';

const FIELDS = [
  { key: 'first', label: 'First name', aliases: ['first', 'first name', 'firstname', 'given name', 'fname', 'first nm'] },
  { key: 'last', label: 'Last name', aliases: ['last', 'last name', 'lastname', 'surname', 'lname', 'family name', 'last nm'] },
  { key: 'name', label: 'Full name', aliases: ['name', 'full name', 'player', 'player name', 'golfer', 'golfer name', 'member', 'member name', 'participant', 'full nm'] },
  { key: 'email', label: 'Email', aliases: ['email', 'e mail', 'email address', 'e mail address', 'mail'] },
  { key: 'phone', label: 'Phone', aliases: ['phone', 'mobile', 'cell', 'phone number', 'cell phone', 'mobile phone', 'mobile number', 'cell number', 'telephone'] },
  { key: 'ghin', label: 'GHIN number', aliases: ['ghin', 'ghin number', 'ghin no', 'ghin id', 'ghin num', 'usga', 'handicap number'] },
  { key: 'handicap', label: 'Handicap Index', aliases: ['handicap', 'hcp', 'index', 'handicap index', 'hi', 'h i', 'hdcp', 'hcp index', 'hcp idx', 'handicap idx'] },
  { key: 'gender', label: 'Gender', aliases: ['gender', 'sex', 'm f', 'mf'] },
  { key: 'role', label: 'Role (Player or Guest)', aliases: ['role', 'type', 'player type', 'golfer type', 'member type', 'membership', 'member or guest', 'guest or member', 'player or guest', 'guest', 'regular'] },
  { key: 'team', label: 'Team', aliases: ['team', 'team name', 'team number', 'team no', 'team num', 'team id', 'foursome', 'squad', 'team captain name'] },
  { key: 'captain', label: 'Captain (Y)', aliases: ['captain', 'capt', 'is captain', 'team captain', 'captain y n', 'captain yn'] },
];
const MAX_TEAM = 4;
const TEAM_NAME_ALIASES = ['team', 'team name', 'team number', 'team no', 'team num', 'team id', 'foursome', 'squad', 'organization', 'company', 'church', 'sponsor'];
// One row per team: "Player 1", "Golfer 2", "Name 3", "Teammate 4", "Captain" (= player 1).
const SLOT_RE = /^(?:player|golfer|participant|teammate|partner|member|name)\s*(\d)(?:\s*(?:name|full name))?$/;
const SLOT_EMAIL_RE = /^(?:(?:player|golfer|participant|teammate|partner|member)\s*(\d)\s*(?:email|e mail|email address)|(?:email|e mail)\s*(\d))$/;
const SLOT_HCP_RE = /^(?:(?:player|golfer|participant|teammate|partner|member)\s*(\d)\s*(?:handicap|hcp|index|handicap index|hi|hdcp)|(?:handicap|hcp|index|hdcp)\s*(\d))$/;
const CAPTAIN_SLOT = ['captain', 'captain name', 'team captain', 'captain full name'];
const CAPTAIN_EMAIL = ['captain email', 'captain e mail', 'captain email address'];
const CAPTAIN_HCP = ['captain handicap', 'captain hcp', 'captain index', 'captain handicap index'];
const TEMPLATE = 'First name,Last name,Email,Phone,GHIN,Handicap Index,Gender,Role\nJohn,Smith,john@example.com,843-555-0100,1234567,12.4,M,Player\nMary,Jones,mary@example.com,843-555-0101,7654321,18.2,W,Guest\n';
const ROLE_LABEL = (r) => (String(r || '').toLowerCase() === 'guest' ? 'Guest' : r === 'Captain' ? 'Captain' : r === 'Organizer' ? 'Organizer' : 'Regular');
const green = '#2d6a4f';
const FILLED_LABEL = { email: 'email', phone: 'phone', ghin: 'GHIN', handicap: 'Handicap Index', gender: 'gender' };

const norm = (s) => String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
const colName = (i) => { let s = ''; let n = i; do { s = String.fromCharCode(65 + (n % 26)) + s; n = Math.floor(n / 26) - 1; } while (n >= 0); return `Column ${s}`; };

/** CSV / TSV text → rows of cells (quotes, doubled quotes and newlines inside quotes handled). */
export function parseDelimited(text) {
  const src = String(text || '').replace(/^﻿/, '');
  const firstLine = src.split(/\r?\n/, 1)[0] || '';
  const delim = (firstLine.match(/\t/g) || []).length > (firstLine.match(/,/g) || []).length ? '\t'
    : (firstLine.match(/;/g) || []).length > (firstLine.match(/,/g) || []).length ? ';' : ',';
  const rows = []; let row = []; let cell = ''; let q = false;
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (q) {
      if (c === '"') { if (src[i + 1] === '"') { cell += '"'; i++; } else q = false; }
      else cell += c;
    } else if (c === '"') q = true;
    else if (c === delim) { row.push(cell); cell = ''; }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && src[i + 1] === '\n') i++;
      row.push(cell); rows.push(row); row = []; cell = '';
    } else cell += c;
  }
  if (cell !== '' || row.length) { row.push(cell); rows.push(row); }
  return rows.map(r => r.map(x => String(x).trim())).filter(r => r.some(x => x !== ''));
}

/** Which column holds which field: from the header when there is one, else guessed from the values. */
export function detect(rows) {
  const first = rows[0] || [];
  const map = {};
  first.forEach((h, i) => {
    const n = norm(h);
    for (const f of FIELDS) if (map[f.key] == null && f.aliases.includes(n)) { map[f.key] = i; break; }
  });
  const hasHeader = ['first', 'last', 'name', 'email'].some(k => map[k] != null);
  if (hasHeader) {
    if (map.name != null && map.first != null && map.last != null) delete map.name;
    return { hasHeader: true, map };
  }
  // No header: guess from the values in the first rows.
  const sample = rows.slice(0, 20);
  const width = Math.max(...rows.map(r => r.length), 0);
  const share = (i, re) => sample.filter(r => re.test(r[i] || '')).length / Math.max(1, sample.filter(r => (r[i] || '') !== '').length);
  const guess = {};
  for (let i = 0; i < width; i++) {
    if (guess.email == null && share(i, /@.+\./) > 0.6) guess.email = i;
    else if (guess.ghin == null && share(i, /^\d{6,9}$/) > 0.6) guess.ghin = i;
    else if (guess.handicap == null && share(i, /^\+?\d{1,2}(\.\d)?$/) > 0.6) guess.handicap = i;
    else if (guess.phone == null && share(i, /^[\d\s().+-]{10,}$/) > 0.6) guess.phone = i;
    else if (guess.gender == null && share(i, /^(m|f|w|male|female)$/i) > 0.8) guess.gender = i;
  }
  const textCols = [];
  for (let i = 0; i < width; i++) if (!Object.values(guess).includes(i) && share(i, /[a-z]/i) > 0.8) textCols.push(i);
  if (textCols.length >= 2) { guess.first = textCols[0]; guess.last = textCols[1]; }
  else if (textCols.length === 1) guess.name = textCols[0];
  return { hasHeader: false, map: guess };
}

/** One golfer's name from the mapped columns. "Smith, John" in a single column becomes "John Smith". */
export function nameOf(r, map) {
  const get = (k) => (map[k] != null ? String(r[map[k]] || '').trim() : '');
  const first = get('first'), last = get('last');
  if (first || last) return `${first} ${last}`.trim();
  const full = get('name');
  const m = full.match(/^([^,]+),\s*(.+)$/);
  return m ? `${m[2]} ${m[1]}`.trim() : full;
}

export function buildRows(rows, hasHeader, map) {
  const body = hasHeader ? rows.slice(1) : rows;
  const get = (r, k) => (map[k] != null ? String(r[map[k]] || '').trim() : '');
  return body.map(r => {
    let team = get(r, 'team');
    if (/^\d+$/.test(team)) team = `Team ${team}`;   // "7" → "Team 7"
    return {
      name: nameOf(r, map), email: get(r, 'email'), phone: get(r, 'phone'),
      ghin: get(r, 'ghin'), handicap: get(r, 'handicap'), gender: get(r, 'gender'), role: get(r, 'role'),
      team, captain: get(r, 'captain'),
    };
  // A row with nothing on it but a team is a held spot; a fully blank row is dropped by the parser.
  });
}

/**
 * One row per team? Look for player columns across the headings (Player 1, Player 2…,
 * or Captain plus Player 2…). Returns { shape: 'team', teamMap } or null.
 * teamMap = { team: col|undefined, slots: [{ name, email, handicap }] } — slot 1 first.
 */
export function detectTeamShape(rows) {
  const first = rows[0] || [];
  const slots = {};
  let team;
  const slot = (n) => (slots[n] = slots[n] || {});
  first.forEach((h, i) => {
    const n = norm(h);
    let m;
    if (CAPTAIN_SLOT.includes(n)) { if (slot(1).name == null) slot(1).name = i; return; }
    if (CAPTAIN_EMAIL.includes(n)) { if (slot(1).email == null) slot(1).email = i; return; }
    if (CAPTAIN_HCP.includes(n)) { if (slot(1).handicap == null) slot(1).handicap = i; return; }
    if ((m = n.match(SLOT_EMAIL_RE))) { const k = Number(m[1] || m[2]); if (slot(k).email == null) slot(k).email = i; return; }
    if ((m = n.match(SLOT_HCP_RE))) { const k = Number(m[1] || m[2]); if (slot(k).handicap == null) slot(k).handicap = i; return; }
    if ((m = n.match(SLOT_RE))) { const k = Number(m[1]); if (slot(k).name == null) slot(k).name = i; return; }
    if (team == null && TEAM_NAME_ALIASES.includes(n)) team = i;
  });
  const named = Object.keys(slots).map(Number).filter(k => slots[k].name != null).sort((a, b) => a - b);
  if (named.length < 2) return null;
  // A "Captain" column only counts as player 1 when Player 2… exist; numbered from 1 either way.
  const list = named.slice(0, MAX_TEAM).map(k => ({ name: slots[k].name, email: slots[k].email, handicap: slots[k].handicap }));
  return { shape: 'team', teamMap: { team, slots: list } };
}

const surnameOf = (name) => { const p = String(name || '').trim().split(/\s+/); return p[p.length - 1] || ''; };

/**
 * One row per team → one row per golfer, each carrying its team. Player 1 is the
 * captain. A blank player column holds a spot (when holdBlanks) — the server names
 * it "<team> — player N". With no Team column the team is "<captain's surname> team".
 */
export function buildTeamRows(rows, hasHeader, teamMap, holdBlanks) {
  const body = hasHeader ? rows.slice(1) : rows;
  const cell = (r, i) => (i != null ? String(r[i] || '').trim() : '');
  const out = [];
  body.forEach((r) => {
    const golfers = teamMap.slots.map(sl => ({ name: nameOf(r, { name: sl.name }), email: cell(r, sl.email), handicap: cell(r, sl.handicap) }));
    const firstNamed = golfers.find(g => g.name);
    let team = cell(r, teamMap.team);
    if (!team && !firstNamed) return;   // an empty line
    if (!team) team = `${surnameOf(firstNamed.name)} team`;
    if (/^\d+$/.test(team)) team = `Team ${team}`;
    golfers.forEach((g) => {
      if (g.name) out.push({ name: g.name, email: g.email, handicap: g.handicap, team, captain: g === firstNamed ? 'Y' : '' });
      else if (holdBlanks) out.push({ name: '', team });
    });
  });
  return out;
}

let xlsxPromise = null;
function loadXlsx() {
  if (typeof window !== 'undefined' && window.XLSX) return Promise.resolve(window.XLSX);
  if (!xlsxPromise) {
    xlsxPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
      s.onload = () => resolve(window.XLSX);
      s.onerror = () => { xlsxPromise = null; reject(new Error('Could not load the Excel reader. Save the sheet as CSV and try again.')); };
      document.head.appendChild(s);
    });
  }
  return xlsxPromise;
}

const fmtTeeTime = (t) => {
  const d = String(t.date || '').slice(0, 10);
  let day = d;
  try { day = new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); } catch (e) { /* keep ISO */ }
  const time = String(t.time || '').slice(0, 5);
  const label = t.event_name || t.title || '';
  return `${day}${time ? ` · ${time}` : ''} · ${t.course || t.course_name || 'Tee time'}${label ? ` · ${label}` : ''}`;
};

export default function PlayerImport({ isOpen, onClose, onImported, eventAccount = false, apiUrl, getToken }) {
  const [step, setStep] = useState('choose');          // choose | check | done
  const [raw, setRaw] = useState(null);                // rows of cells
  const [fileName, setFileName] = useState('');
  const [pasted, setPasted] = useState('');
  const [hasHeader, setHasHeader] = useState(true);
  const [map, setMap] = useState({});
  const [role, setRole] = useState(eventAccount ? 'guest' : 'Player');
  const [teeTimes, setTeeTimes] = useState([]);
  const [teetimeId, setTeetimeId] = useState('');
  const [preview, setPreview] = useState(null);        // dry-run answer
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [shape, setShape] = useState('golfer');        // golfer | team  (Oct 10, 2026)
  const [teamMap, setTeamMap] = useState({ team: undefined, slots: [] });
  const [holdBlanks, setHoldBlanks] = useState(true);
  const fileRef = useRef(null);
  const seq = useRef(0);

  const request = async (path, method = 'GET', body) => {
    const headers = { 'Content-Type': 'application/json' };
    const t = getToken && getToken();
    if (t) headers.Authorization = `Bearer ${t}`;
    const r = await fetch(`${apiUrl}${path}`, { method, headers, credentials: 'include', body: body ? JSON.stringify(body) : undefined });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { const e = new Error(d.error || d.message || `The server answered ${r.status}.`); e.code = d.code; throw e; }
    return d;
  };

  // Reset each time the window opens; load upcoming tee times for "Also add them to".
  useEffect(() => {
    if (!isOpen) return;
    setStep('choose'); setRaw(null); setFileName(''); setPasted(''); setPreview(null); setError(''); setResult(null);
    setRole(eventAccount ? 'guest' : 'Player'); setTeetimeId('');
    // Nov 2, 2026 (Dennis — Turkey Shoot load): "It defaults to just add to players. Should
    // default to the event." GET /teetimes doesn't say which tee time is an event, so the
    // org's /events list is read alongside it. Any org with exactly one upcoming event gets
    // that event picked (not only Event Pass accounts); an event account with a single
    // upcoming tee time still gets that one. The organizer can still choose another.
    Promise.all([
      request('/teetimes'),
      request('/events').catch(() => null),
    ]).then(([d, ev]) => {
      const list = (Array.isArray(d) ? d : (d.teetimes || d.teeTimes || [])).filter(t => t && t.date);
      const today = new Date(); const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const upcoming = list.filter(t => String(t.date).slice(0, 10) >= iso).sort((a, b) => `${a.date}${a.time}`.localeCompare(`${b.date}${b.time}`));
      setTeeTimes(upcoming);
      const evList = Array.isArray(ev) ? ev : (ev && Array.isArray(ev.events) ? ev.events : []);
      const eventTeetimes = new Set(evList.map(e => e && e.teetime_id).filter(Boolean).map(String));
      const events = upcoming.filter(t => eventTeetimes.has(String(t.id)) || t.is_event || t.event_name || t.event_id);
      if (events.length === 1) setTeetimeId(events[0].id);
      else if (eventAccount && upcoming.length === 1) setTeetimeId(upcoming[0].id);
    }).catch(() => setTeeTimes([]));
  }, [isOpen]);   // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => (!raw ? []
    : shape === 'team' ? buildTeamRows(raw, hasHeader, teamMap, holdBlanks)
    : buildRows(raw, hasHeader, map)), [raw, hasHeader, map, shape, teamMap, holdBlanks]);
  const nameReady = shape === 'team'
    ? teamMap.slots.length > 0 && teamMap.slots[0].name != null
    : (map.name != null || map.first != null || map.last != null);
  const hasTeams = shape === 'team' || map.team != null;
  const teamSize = shape === 'team' ? teamMap.slots.length : null;

  // Every change on the Check step asks the server what the import would do.
  useEffect(() => {
    if (step !== 'check' || !rows.length || !nameReady) { setPreview(null); return; }
    const mine = ++seq.current;
    setChecking(true); setError('');
    const t = setTimeout(() => {
      request('/players/import', 'POST', { rows, role, teetime_id: teetimeId || null, team_size: teamSize, dry_run: true })
        .then(d => { if (seq.current === mine) setPreview(d); })
        .catch(e => { if (seq.current === mine) { setPreview(null); setError(e.message); } })
        .finally(() => { if (seq.current === mine) setChecking(false); });
    }, 250);
    return () => clearTimeout(t);
  }, [step, rows, role, teetimeId, nameReady, teamSize]);   // eslint-disable-line react-hooks/exhaustive-deps

  const take = (cells, name) => {
    if (!cells.length) { setError('That list looks empty.'); return; }
    const d = detect(cells);
    const t = detectTeamShape(cells);
    setRaw(cells); setFileName(name); setHasHeader(d.hasHeader || !!t); setMap(d.map); setError('');
    setShape(t ? 'team' : 'golfer');
    setTeamMap(t ? t.teamMap : { team: undefined, slots: [{}, {}, {}, {}] });
    setStep('check');
  };
  const onFile = async (file) => {
    if (!file) return;
    setError('');
    const n = file.name.toLowerCase();
    try {
      if (n.endsWith('.xlsx') || n.endsWith('.xls')) {
        const XLSX = await loadXlsx();
        const wb = XLSX.read(await file.arrayBuffer());
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const cells = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '' })
          .map(r => r.map(x => String(x == null ? '' : x).trim())).filter(r => r.some(x => x !== ''));
        take(cells, file.name);
      } else if (n.endsWith('.csv') || n.endsWith('.tsv') || n.endsWith('.txt')) {
        take(parseDelimited(await file.text()), file.name);
      } else {
        setError('Choose a CSV or Excel file (.csv, .xlsx, .xls), or paste the list instead.');
      }
    } catch (e) { setError(e.message || 'Could not read that file.'); }
  };
  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'tto-player-import-template.csv';
    document.body.appendChild(a); a.click(); a.remove();
  };
  const doImport = async () => {
    setBusy(true); setError('');
    try {
      const d = await request('/players/import', 'POST', { rows, role, teetime_id: teetimeId || null, team_size: teamSize });
      setResult(d); setStep('done');
      if (onImported) onImported(d);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  if (!isOpen) return null;
  const width = raw ? Math.max(...raw.map(r => r.length)) : 0;
  const columns = Array.from({ length: width }, (_, i) => (hasHeader && raw && raw[0][i] ? `${raw[0][i]}` : colName(i)));
  const btn = (primary, disabled) => ({ padding: '10px 16px', borderRadius: 10, border: primary ? 'none' : '1px solid #d1d5db', background: disabled ? '#9ca3af' : primary ? green : '#fff', color: primary ? '#fff' : '#374151', fontSize: 15, fontWeight: 700, cursor: disabled ? 'default' : 'pointer' });
  const head = { fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 6px' };
  const sum = preview && preview.summary;
  const statusOf = (line) => preview && (preview.rows || []).find(x => x.line === line);
  const willImport = sum ? sum.created + sum.matched : 0;
  const tt = teeTimes.find(t => t.id === teetimeId);

  return (
    <div onClick={() => !busy && onClose()} data-testid="player-import"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ width: '100%', maxWidth: 760, maxHeight: '94vh', overflowY: 'auto', background: '#fff', borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}>
        <div style={{ background: 'linear-gradient(135deg, #2d6a4f, #1b4332)', color: '#fff', padding: '16px 20px', borderRadius: '16px 16px 0 0', display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 19 }}>📥 Import players</div>
            <div style={{ fontSize: 13, opacity: 0.9 }}>{step === 'choose' ? 'Step 1 of 2 · Choose your list' : step === 'check' ? 'Step 2 of 2 · Check and import' : 'Done'}</div>
          </div>
          <button type="button" onClick={() => !busy && onClose()} aria-label="Close" style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ padding: '18px 20px 20px', display: 'grid', gap: 16 }}>
          {step === 'choose' && (
            <>
              <p style={{ margin: 0, fontSize: 15, color: '#374151', lineHeight: 1.55 }}>
                Bring in a whole list at once: your event&rsquo;s field, a club roster or a registration export. A name is all
                each golfer needs; email, phone, GHIN number and Handicap Index come along if your list has them.
              </p>
              <div style={{ border: '2px dashed #86efac', borderRadius: 12, padding: 18, textAlign: 'center', background: '#f0fdf4' }}
                onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files && e.dataTransfer.files[0]); }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#14532d', marginBottom: 8 }}>A spreadsheet file (CSV or Excel)</div>
                <input ref={fileRef} type="file" accept=".csv,.tsv,.txt,.xlsx,.xls" style={{ display: 'none' }} data-testid="import-file"
                  onChange={(e) => { onFile(e.target.files && e.target.files[0]); e.target.value = ''; }} />
                <button type="button" style={btn(true)} onClick={() => fileRef.current && fileRef.current.click()}>Choose a file</button>
                <div style={{ fontSize: 12.5, color: '#4b5563', marginTop: 8 }}>
                  or drag it here · <button type="button" onClick={downloadTemplate} style={{ background: 'none', border: 'none', padding: 0, color: green, textDecoration: 'underline', cursor: 'pointer', fontSize: 'inherit' }}>download a blank template</button>
                </div>
              </div>
              <div>
                <div style={head}>Or paste the rows</div>
                <textarea value={pasted} onChange={(e) => setPasted(e.target.value)} rows={6} data-testid="import-paste"
                  placeholder={'Copy the rows in your spreadsheet (headings too) and paste them here.\nFirst name\tLast name\tEmail\nJohn\tSmith\tjohn@example.com'}
                  style={{ width: '100%', boxSizing: 'border-box', padding: 10, border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, fontFamily: 'ui-monospace, Menlo, monospace' }} />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                  <button type="button" style={btn(false, !pasted.trim())} disabled={!pasted.trim()} onClick={() => take(parseDelimited(pasted), 'Pasted list')}>Use pasted list</button>
                </div>
              </div>
            </>
          )}

          {step === 'check' && (
            <>
              <div style={{ fontSize: 14, color: '#374151' }}>
                <strong>{fileName}</strong> · {rows.length} {rows.length === 1 ? 'row' : 'rows'}{' '}
                <button type="button" onClick={() => setStep('choose')} style={{ background: 'none', border: 'none', padding: 0, color: green, textDecoration: 'underline', cursor: 'pointer', fontSize: 13 }}>choose a different list</button>
              </div>

              <div>
                <div style={head}>How is your list laid out?</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 8 }}>
                  {[
                    { v: 'golfer', t: 'One row per golfer', s: 'A Team column (name or number) keeps teams together, if your list has one.' },
                    { v: 'team', t: 'One row per team', s: 'The players across the row: Player 1, Player 2, … Player 1 is the captain.' },
                  ].map(o => (
                    <label key={o.v} data-testid={`shape-${o.v}`} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 10px', borderRadius: 8, cursor: 'pointer', border: shape === o.v ? `2px solid ${green}` : '1px solid #e5e7eb', background: shape === o.v ? '#f0fdf4' : '#fff' }}>
                      <input type="radio" name="import-shape" checked={shape === o.v} onChange={() => setShape(o.v)} style={{ marginTop: 3 }} />
                      <span><strong style={{ fontSize: 14 }}>{o.t}</strong><span style={{ display: 'block', fontSize: 12.5, color: '#6b7280', lineHeight: 1.4 }}>{o.s}</span></span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <div style={head}>Your columns</div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', marginBottom: 8 }}>
                  <input type="checkbox" checked={hasHeader} onChange={(e) => setHasHeader(e.target.checked)} />
                  The first row is headings, not a {shape === 'team' ? 'team' : 'golfer'}
                </label>
                {shape === 'team' ? (() => {
                  const pick = (value, onChange, testid) => (
                    <select value={value != null ? String(value) : ''} data-testid={testid}
                      onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
                      style={{ width: '100%', padding: '6px 8px', border: `1px solid ${value != null ? '#86efac' : '#d1d5db'}`, borderRadius: 6, fontSize: 13, background: value != null ? '#f0fdf4' : '#fff' }}>
                      <option value="">— not in my list —</option>
                      {columns.map((c, i) => <option key={i} value={String(i)}>{c}</option>)}
                    </select>
                  );
                  const setSlot = (k, field, v) => setTeamMap(tm => ({ ...tm, slots: tm.slots.map((sl, i) => (i === k ? { ...sl, [field]: v } : sl)) }));
                  const setCount = (n) => setTeamMap(tm => ({ ...tm, slots: Array.from({ length: n }, (_, i) => tm.slots[i] || {}) }));
                  const cellStyle = { padding: '4px 6px', verticalAlign: 'middle' };
                  return (
                    <div style={{ display: 'grid', gap: 10 }} data-testid="team-columns">
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <label style={{ display: 'grid', gap: 3, fontSize: 12.5, color: '#374151', fontWeight: 600, minWidth: 210 }}>
                          Team name (optional)
                          {pick(teamMap.team, (v) => setTeamMap(tm => ({ ...tm, team: v })), 'map-teamname')}
                        </label>
                        <label style={{ display: 'grid', gap: 3, fontSize: 12.5, color: '#374151', fontWeight: 600 }}>
                          Players per team
                          <select value={teamMap.slots.length} onChange={(e) => setCount(Number(e.target.value))} data-testid="team-size"
                            style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}>
                            {[2, 3, 4].map(n => <option key={n} value={n}>{n}</option>)}
                          </select>
                        </label>
                      </div>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ borderCollapse: 'collapse', fontSize: 12.5, minWidth: 520 }}>
                          <thead>
                            <tr style={{ textAlign: 'left', color: '#374151' }}>
                              <th style={cellStyle} /><th style={cellStyle}>Name</th><th style={cellStyle}>Email</th><th style={cellStyle}>Handicap Index</th>
                            </tr>
                          </thead>
                          <tbody>
                            {teamMap.slots.map((sl, k) => (
                              <tr key={k}>
                                <td style={{ ...cellStyle, fontWeight: 700, whiteSpace: 'nowrap' }}>{k === 0 ? 'Player 1 (captain)' : `Player ${k + 1}`}</td>
                                <td style={cellStyle}>{pick(sl.name, (v) => setSlot(k, 'name', v), `map-slot${k + 1}-name`)}</td>
                                <td style={cellStyle}>{pick(sl.email, (v) => setSlot(k, 'email', v), `map-slot${k + 1}-email`)}</td>
                                <td style={cellStyle}>{pick(sl.handicap, (v) => setSlot(k, 'handicap', v), `map-slot${k + 1}-hcp`)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: '#374151' }}>
                        <input type="checkbox" checked={holdBlanks} onChange={(e) => setHoldBlanks(e.target.checked)} style={{ marginTop: 2 }} data-testid="hold-blanks" />
                        <span>A blank player holds a spot for a teammate whose name is coming later
                          <span style={{ display: 'block', fontSize: 12, color: '#6b7280' }}>Untick if a blank means the team is short (you&rsquo;ll pair them up).</span></span>
                      </label>
                      {!nameReady && <div style={{ color: '#b91c1c', fontSize: 13 }}>Pick the column with Player 1&rsquo;s name.</div>}
                    </div>
                  );
                })() : (
                <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 8 }}>
                  {FIELDS.map(f => (
                    <label key={f.key} style={{ display: 'grid', gap: 3, fontSize: 12.5, color: '#374151', fontWeight: 600 }}>
                      {f.label}
                      <select value={map[f.key] != null ? String(map[f.key]) : ''} data-testid={`map-${f.key}`}
                        onChange={(e) => setMap({ ...map, [f.key]: e.target.value === '' ? undefined : Number(e.target.value) })}
                        style={{ padding: '6px 8px', border: `1px solid ${map[f.key] != null ? '#86efac' : '#d1d5db'}`, borderRadius: 6, fontSize: 13, fontWeight: 400, background: map[f.key] != null ? '#f0fdf4' : '#fff' }}>
                        <option value="">— not in my list —</option>
                        {columns.map((c, i) => <option key={i} value={String(i)}>{c}</option>)}
                      </select>
                    </label>
                  ))}
                </div>
                {!nameReady && <div style={{ color: '#b91c1c', fontSize: 13, marginTop: 6 }}>Pick the column with the golfers&rsquo; names (Full name, or First and Last).</div>}
                {map.team != null && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6, lineHeight: 1.45 }}>Rows with the same Team are one team. A row with a Team but no name holds a spot for a teammate whose name is coming later.</div>}
                </>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 }}>
                <div>
                  <div style={head}>{map.role != null ? 'Rows with no role in your list come in as' : 'Add them as'}</div>
                  {[
                    { v: 'guest', t: 'Guests', s: 'Free, and never count toward your plan. They see only the rounds they’re invited to. Right for an event field.' },
                    { v: 'Player', t: 'Regular players', s: 'Get your new tee time invitations and see all your tee times. Count toward your plan.' },
                  ].map(o => (
                    <label key={o.v} data-testid={`role-${o.v}`} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 10px', borderRadius: 8, marginBottom: 6, cursor: 'pointer', border: role === o.v ? `2px solid ${green}` : '1px solid #e5e7eb', background: role === o.v ? '#f0fdf4' : '#fff' }}>
                      <input type="radio" name="import-role" checked={role === o.v} onChange={() => setRole(o.v)} style={{ marginTop: 3 }} />
                      <span><strong style={{ fontSize: 14 }}>{o.t}</strong><span style={{ display: 'block', fontSize: 12.5, color: '#6b7280', lineHeight: 1.4 }}>{o.s}</span></span>
                    </label>
                  ))}
                  <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.45 }}>
                    {map.role != null ? 'Each row’s Role column (Player, Regular, Member, Captain or Guest) decides it. ' : 'Mixing regulars and guests? Add a Role column (Player or Guest) to your list. '}
                    Golfers already in your list keep their role.
                  </div>
                </div>
                <div>
                  <div style={head}>Also put them on a tee time (optional)</div>
                  <select value={teetimeId} onChange={(e) => setTeetimeId(e.target.value)} data-testid="import-teetime"
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13.5 }}>
                    <option value="">— Just add them to my players —</option>
                    {teeTimes.map(t => <option key={t.id} value={t.id}>{fmtTeeTime(t)}</option>)}
                  </select>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6, lineHeight: 1.45 }}>
                    They&rsquo;re confirmed while there&rsquo;s room (its spots, and for an event its Event Pass field size), then waitlisted. Nobody is emailed.
                  </div>
                </div>
              </div>

              <div data-testid="import-summary" style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px', fontSize: 14, color: '#374151', lineHeight: 1.6 }}>
                {checking && !sum && 'Checking your list…'}
                {sum && (
                  <>
                    {sum.created_regulars > 0 && <><strong>{sum.created_regulars}</strong> new regular {sum.created_regulars === 1 ? 'player' : 'players'} · </>}
                    <strong>{sum.created_guests}</strong> new {sum.created_guests === 1 ? 'guest' : 'guests'}
                    {' · '}<strong>{sum.matched}</strong> already in your list{sum.updated ? ` (${sum.updated} ${sum.updated === 1 ? 'gets' : 'get'} missing details filled in)` : ''}
                    {sum.skipped ? <> · <strong>{sum.skipped}</strong> skipped</> : null}
                    {sum.teetime && tt && (
                      <div>On {fmtTeeTime(tt)}: <strong>{sum.teetime.confirmed}</strong> confirmed{sum.teetime.waitlist ? <>, <strong style={{ color: '#92400e' }}>{sum.teetime.waitlist} waitlisted</strong> (no room{sum.teetime.field_cap ? `: your Event Pass covers ${sum.teetime.field_cap}` : ''})</> : null}{sum.teetime.already ? `, ${sum.teetime.already} already on it` : ''}.</div>
                    )}
                    {sum.teams && (
                      <div data-testid="import-teams">
                        👥 <strong>{sum.teams.teams}</strong> {sum.teams.teams === 1 ? 'team' : 'teams'} of {sum.teams.size}
                        {' · '}<strong>{sum.teams.complete}</strong> complete
                        {sum.teams.names_to_come ? <> · <strong>{sum.teams.names_to_come}</strong> with names to come ({sum.teams.held_spots} held {sum.teams.held_spots === 1 ? 'spot' : 'spots'})</> : null}
                        {sum.teams.short.length ? <> · <strong style={{ color: '#92400e' }}>{sum.teams.short.length} short</strong>: {sum.teams.short.map(t => `${t.name} (${t.golfers})`).join(', ')}</> : null}
                      </div>
                    )}
                    {hasTeams && !teetimeId && (
                      <div style={{ color: '#92400e' }} data-testid="import-teams-need-teetime">Choose the event&rsquo;s tee time above to keep the teams together. Without one they come in as single players.</div>
                    )}
                  </>
                )}
              </div>

              {sum && sum.teams && sum.teams.too_big.length > 0 && (
                <div data-testid="import-team-too-big" style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '12px 14px', color: '#991b1b', fontSize: 14, lineHeight: 1.55 }}>
                  <strong>A team can have up to {MAX_TEAM} golfers.</strong> Too many in: {sum.teams.too_big.map(t => `${t.name} (${t.golfers})`).join(', ')}. Fix your list and choose it again.
                </div>
              )}
              {preview && preview.plan && (
                <div data-testid="import-plan-over" style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '12px 14px', color: '#991b1b', fontSize: 14, lineHeight: 1.55 }}>
                  <div style={{ fontWeight: 800, marginBottom: 2 }}>
                    {preview.plan.over} regular {preview.plan.over === 1 ? 'player' : 'players'} over your {preview.plan.tier_name} plan
                  </div>
                  {preview.plan.message}
                  <div style={{ marginTop: 6, fontSize: 13 }}>
                    To upgrade: <strong>🏢 Settings → 💳 Subscription &amp; Billing</strong>.
                    {map.role == null ? <> Or bring them in as <strong>Guests</strong> above.</> : <> Or edit the Role column in your list and choose it again.</>}
                  </div>
                </div>
              )}
              <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 10 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }} data-testid="import-preview">
                  <thead>
                    <tr style={{ background: '#f3f4f6', textAlign: 'left' }}>
                      {['', 'Name', ...(hasTeams ? ['Team'] : []), 'Email', 'GHIN', 'HI', 'Role', 'Status'].map(h => <th key={h} style={{ padding: '7px 9px', fontWeight: 700, color: '#374151', whiteSpace: 'nowrap' }}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 50).map((r, i) => {
                      const s = statusOf(i + 1);
                      const tag = !s ? '…' : s.held ? (s.action === 'match' ? 'Held spot · already in your list' : 'Held spot') : s.action === 'new' ? 'New' : s.action === 'match' ? `Already in your list${s.filled && s.filled.length ? ' · fills in ' + s.filled.map(k => FILLED_LABEL[k] || k).join(', ') : ''}` : `Skipped: ${s.reason}`;
                      const color = !s ? '#6b7280' : s.action === 'new' ? '#166534' : s.action === 'match' ? '#1d4ed8' : '#b91c1c';
                      return (
                        <tr key={i} style={{ borderTop: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '6px 9px', color: '#9ca3af' }}>{i + 1}</td>
                          <td style={{ padding: '6px 9px', fontWeight: 600 }}>{r.name || (s && s.held ? <span style={{ color: '#6b7280', fontStyle: 'italic' }}>{s.name}</span> : <span style={{ color: '#b91c1c' }}>(no name)</span>)}</td>
                          {hasTeams && <td style={{ padding: '6px 9px', whiteSpace: 'nowrap' }}>{r.team || ''}{s && s.captain ? <span style={{ color: '#6b7280' }}> · captain</span> : null}</td>}
                          <td style={{ padding: '6px 9px' }}>{r.email}</td>
                          <td style={{ padding: '6px 9px' }}>{r.ghin}</td>
                          <td style={{ padding: '6px 9px' }}>{r.handicap}</td>
                          <td style={{ padding: '6px 9px', whiteSpace: 'nowrap', color: s && s.role && String(s.role).toLowerCase() !== 'guest' ? '#1d4ed8' : '#374151' }}>{s && s.role ? ROLE_LABEL(s.role) : ''}</td>
                          <td style={{ padding: '6px 9px', color, whiteSpace: 'nowrap' }}>{tag}{s && s.note ? <span style={{ color: '#92400e' }}> · {s.note}</span> : null}{s && s.teetime === 'waitlist' ? <span style={{ color: '#92400e' }}> · waitlist</span> : null}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {rows.length > 50 && <div style={{ fontSize: 12, color: '#6b7280', padding: '6px 9px' }}>Showing the first 50 of {rows.length}.</div>}
              </div>

              {error && <div style={{ color: '#b91c1c', fontSize: 14 }} data-testid="import-error">{error}</div>}
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                <button type="button" style={btn(false)} onClick={() => setStep('choose')}>Back</button>
                <button type="button" data-testid="import-go" style={btn(true, busy || !sum || !willImport || !!(preview && preview.plan) || !!(sum && sum.teams && sum.teams.too_big.length))} disabled={busy || !sum || !willImport || !!(preview && preview.plan) || !!(sum && sum.teams && sum.teams.too_big.length)} onClick={doImport}>
                  {busy ? 'Importing…' : `Import ${willImport} ${willImport === 1 ? 'golfer' : 'golfers'}`}
                </button>
              </div>
            </>
          )}

          {step === 'choose' && error && <div style={{ color: '#b91c1c', fontSize: 14 }} data-testid="import-error">{error}</div>}

          {step === 'done' && result && (
            <div data-testid="import-done" style={{ display: 'grid', gap: 12 }}>
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '14px 16px', color: '#14532d', fontSize: 15, lineHeight: 1.6 }}>
                <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 4 }}>✅ Import complete</div>
                <div><strong>{result.summary.created}</strong> added ({result.summary.created_regulars} regular, {result.summary.created_guests} {result.summary.created_guests === 1 ? 'guest' : 'guests'}) · <strong>{result.summary.updated}</strong> updated · <strong>{result.summary.skipped}</strong> skipped</div>
                {result.summary.teetime && (
                  <div>On the tee time: <strong>{result.summary.teetime.confirmed}</strong> confirmed{result.summary.teetime.waitlist ? `, ${result.summary.teetime.waitlist} waitlisted` : ''}{result.summary.teetime.already ? `, ${result.summary.teetime.already} were already on it` : ''}.</div>
                )}
                {result.summary.teams && (
                  <div>👥 <strong>{result.summary.teams.teams}</strong> {result.summary.teams.teams === 1 ? 'team' : 'teams'}{result.summary.teams.held_spots ? `, ${result.summary.teams.held_spots} held ${result.summary.teams.held_spots === 1 ? 'spot' : 'spots'}` : ''}.</div>
                )}
              </div>
              {result.summary.teams && (
                <div style={{ fontSize: 13.5, color: '#4b5563', lineHeight: 1.55 }} data-testid="import-done-teams">
                  On <strong>⛳ Pairings</strong>, press <strong>👥 By team</strong> to give each team its own group (and, for a shotgun, its own starting hole).
                  {result.summary.teams.held_spots ? <> A held spot is a guest named like &ldquo;{(result.rows.find(x => x.held) || {}).name || 'Team — player 2'}&rdquo;: when you know the golfer, edit that player and type his name and email.</> : null}
                </div>
              )}
              <div style={{ fontSize: 13.5, color: '#4b5563', lineHeight: 1.55 }}>
                For a net game, each golfer needs a Handicap Index and a preferred tee: check them in the players list (edit a golfer).
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button type="button" style={btn(true)} onClick={onClose}>Done</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
